import React, { useState } from 'react';
import './ContactPage.css';
import { sendPrivateOfficeEnquiry } from '../../services/emailService';
import { OtpLoginStepper } from '../auth/OtpLoginStepper';

interface ContactPageProps {
  onNavigateToMemberships?: () => void;
  onOpenSeatMap?: () => void;
  onBack?: () => void;
  onLoginSuccess?: () => void;
}

export const ContactPage: React.FC<ContactPageProps> = ({ onNavigateToMemberships, onOpenSeatMap, onBack, onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    workspace: '',
    hearAbout: '',
    consent: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [banner, setBanner] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);
  const bannerTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBanner = (message: string, type: 'success' | 'warning' | 'error') => {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    setBanner({ message, type });
    bannerTimerRef.current = setTimeout(() => setBanner(null), 10000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Send private office enquiry email
      const emailSent = await sendPrivateOfficeEnquiry(formData);
      
      if (emailSent) {
        console.log('Private office enquiry email sent successfully');
        showBanner("Thank you for your enquiry! We'll contact you within 24 hours.", 'success');
      } else {
        console.log('Failed to send private office enquiry email');
        showBanner('Enquiry submitted, but there was an issue sending the confirmation email.', 'warning');
      }
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        workspace: '',
        hearAbout: '',
        consent: false
      });
      
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      showBanner('There was an error submitting your enquiry. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {banner && (
        <div className={`enquiry-banner enquiry-banner--${banner.type}`}>
          <span className="enquiry-banner__icon">
            {banner.type === 'success' ? '✓' : banner.type === 'warning' ? '⚠' : '✕'}
          </span>
          <span className="enquiry-banner__message">{banner.message}</span>
          <button className="enquiry-banner__close" onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="two-column-layout">
        {/* Left Column - Form */}
        <div className="left-column">
          <div className="page-header">
            <h1>Looking for a Private Office Solution?</h1>
            <p>Get in touch with our workspace experts to find the perfect private office solution for your team. We'll help you discover flexible workspace options tailored to your business needs.</p>
          </div>
          
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-field">
              <input
                type="text"
                name="firstName"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-field">
              <input
                type="text"
                name="lastName"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-field">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-field">
              <input
                type="tel"
                name="phone"
                placeholder="Mobile number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-field">
              <select name="workspace" value={formData.workspace} onChange={handleChange} required>
                <option value="" disabled>Required workspace</option>
                <option value="1-5">1-5 people</option>
                <option value="6-10">6-10 people</option>
                <option value="11-20">11-20 people</option>
                <option value="20+">20+ people</option>
              </select>
            </div>
            
            <div className="form-field">
              <select 
                name="hearAbout" 
                value={formData.hearAbout} 
                onChange={handleChange}
                className={formData.hearAbout === '' ? 'placeholder' : ''}
              >
                <option value="" disabled>How did you hear about us?</option>
                <option value="google">Google Search</option>
                <option value="social">Social Media</option>
                <option value="referral">Referral</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="checkbox-row">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                required
              />
              <label htmlFor="consent">
                I agree to the processing of my personal data and <a href="/privacy/privacy_policy.html" className="text-[#49685E] hover:text-[#1F473E]">Privacy Policy</a>
              </label>
            </div>
            
            <button 
              type="submit" 
              className={`px-8 py-3 rounded transition-colors shadow-lg ${
                formData.consent 
                  ? 'bg-[#1F473E] text-[#F1ECDC] hover:bg-[#49685E] shadow-[#1F473E]/20' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-gray-300/20'
              }`}
              disabled={isLoading || !formData.consent}
            >
              <span>{isLoading ? 'Submitting...' : 'Submit →'}</span>
            </button>
          </form>
        </div>
        
        {/* Right Column - Info */}
        <div className="right-column">
          <div className="info-section">
            <h3>Workspace On-Demand</h3>
            <p>Access flexible workspace solutions whenever you need them. From hot desks to private offices, we have options for every business need.</p>
            <div className="cta-buttons">
              <button 
                className="px-8 py-3 bg-[#1F473E] text-[#F1ECDC] rounded hover:bg-[#49685E] transition-colors shadow-lg shadow-[#1F473E]/20" 
                onClick={() => {
                  console.log('Book Now clicked - Opening Seat Map');
                  onOpenSeatMap?.();
                }}
              >
                <span>Book Now</span>
              </button>
            </div>
          </div>
          
          <div className="divider"></div>
          
          <div className="phone-section">
            <h4>Rather talk over the phone?</h4>
            <a href="tel:+91 9686663056" className="phone-number">+91 9686663056</a>
            <p className="availability">Available Mon-Fri, 9 AM - 6 PM IST</p>
          </div>
          
          <div className="divider"></div>
          
          <div className="members-section">
            <h4>Already a member?</h4>
            <p>Access your member portal for bookings and account management.</p>
            <div className="member-buttons">
              <button 
                className="px-8 py-3 bg-[#1F473E] text-[#F1ECDC] rounded hover:bg-[#49685E] transition-colors shadow-lg shadow-[#1F473E]/20"
                onClick={() => setShowLogin(true)}
              >
                <span>Login</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showLogin && (
        <OtpLoginStepper
          onClose={() => setShowLogin(false)}
          onLoginSuccess={() => {
            console.log('User logged in successfully from ContactPage');
            setShowLogin(false);
            onLoginSuccess?.();
          }}
        />
      )}
    </div>
  );
};